import {
  action, computed, makeAutoObservable, observable, runInAction,
} from 'mobx';
import { AppDtoAgentEnum } from '@coretava/api/core-service';
import isEqual from 'lodash/isEqual';
import { AnalyticsFactory } from '@gamiphy/analytics';
import { CartApi, ProductsApi } from '@coretava/api/ecommerce-service';
import { EventsStore } from '@gamiphy/loyalty-station-desktop/store/events';
import { DisplayString, getDisplayStringValue } from '@coretava/i18n';
import { i18next } from '@gamiphy/react-i18n';
import { EcommerceCartFactory } from './platforms';
import type { CartData, CartSdkConfig } from './types';

export class CartSdkStore {
  private static readonly DOCUMENT_EVENTS = {
    updated: 'coretava:cart:updated',
  };

  @observable public data?: CartData = undefined;

  constructor(
    private readonly config: CartSdkConfig,
  ) {
    makeAutoObservable(this);

    void this.initialize();
  }

  @computed
  private getApi(token?: string): CartApi {
    return new CartApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: token,
    });
  }

  @computed
  private getProductApi(token?: string): ProductsApi {
    return new ProductsApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: token,
    });
  }

  /**
   * @deprecated
   * @param agent
   */
  public static getLegacyCartSdk(agent: AppDtoAgentEnum): CartSdkStore {
    return new CartSdkStore(EcommerceCartFactory.getInstance(agent));
  }

  @action
  public async onCartUpdate(): Promise<void> {
    const [
      { AppStore },
      { UserStore },
    ] = await Promise.all([
      import('@gamiphy/loyalty-station-desktop/store/app'),
      import('@gamiphy/loyalty-station-desktop/store/user'),
      this.loadCart(),
    ]);

    if (this.data) {
      void this.getApi(UserStore.token).cartControllerCreateOrUpdate(AppStore.app.id, {
        id: this.data.id,
        user: UserStore.user?.id,
        customer: (await AnalyticsFactory.getInstance()).customerId,
        total: this.data.total,
        items: this.data.items.map((i) => ({
          quantity: i.quantity,
          price: i.price,
          externalId: i.externalId,
          category: i.category,
          description: i.description,
        })),
      });
    }

    document.dispatchEvent(new CustomEvent(CartSdkStore.DOCUMENT_EVENTS.updated));
  }

  @action
  public async onAddToCard(externalProductId: string): Promise<void> {
    const [
      { AppStore },
      { UserStore },
    ] = await Promise.all([
      import('@gamiphy/loyalty-station-desktop/store/app'),
      import('@gamiphy/loyalty-station-desktop/store/user'),
    ]);

    const [{ data: { product } }] = await Promise.all([
      this.getProductApi(UserStore.token).productsControllerGetByExternalId(AppStore.app.id, externalProductId),
      this.onCartUpdate(),
    ]);

    if (this.data?.id) {
      void EventsStore.createEvent({
        type: 'addedToCart',
        product: {
          id: product.id,
          externalId: product.externalId,
          name: getDisplayStringValue(i18next, product.name as DisplayString),
          price: product.price,
          categories: product.categories,
          globalCategories: product.globalCategories ?? [],
          brand: product.brand ?? undefined,
          globalBrand: product.globalBrand ?? undefined,
          globalProduct: product.globalProduct,
        },
        cart: {
          id: this.data.id,
          total: this.data.total,
        },
      });
    }
  }

  @action
  public async onRemoveFromCart(): Promise<void> {
    await this.onCartUpdate();
  }

  @action
  public async onCartComplete(): Promise<void> {
    const [
      { AppStore },
      { UserStore },
    ] = await Promise.all([
      import('@gamiphy/loyalty-station-desktop/store/app'),
      import('@gamiphy/loyalty-station-desktop/store/user'),
    ]);

    await this.getApi(UserStore.token).cartControllerComplete(AppStore.app.id, undefined, (await AnalyticsFactory.getInstance()).customerId);
  }

  @action
  public async applyDiscount(code: string): Promise<{ success: boolean }> {
    if (this.data) {
      const response = await this.config.onApplyDiscount(this.data, code);
      if (response.success) {
        await this.loadCart();
      }

      return response;
    }
    return {
      success: false,
    };
  }

  @action
  public async removeDiscount(code: string): Promise<{ success: boolean }> {
    // Skip for platforms doesn't have onRemoveDiscount implemented
    if (!this.config.onRemoveDiscount) {
      return { success: true };
    }

    if (this.data) {
      const response = await this.config.onRemoveDiscount(this.data, code);
      if (response.success) {
        await this.loadCart();
      }

      return response;
    }
    return {
      success: false,
    };
  }

  @action
  private async loadCart(): Promise<void> {
    const data = await this.config.onLoadCart();
    if (!isEqual(data, this.data)) {
      runInAction(() => {
        this.data = data;
      });
    }
  }

  public addCartUpdatedListener(cb: () => void): void {
    document.addEventListener(CartSdkStore.DOCUMENT_EVENTS.updated, cb);
  }

  @action
  private async initialize(): Promise<void> {
    await this.loadCart();
  }
}
